import { useState } from 'react'
import { requestObfuscation } from '../utils/requestObfuscation'

interface HTSOption {
  htsCode: string
  description: string
  fullTitle?: string
  confidence: number
  reasoning: string
  comparativeReasoning?: string
  dutyRate: string
  formattedHtsCode: string
  dutyInfo?: any
  customsFees?: Array<{
    lineDescription: string
    lineAmount: number
  }>
  totalDutyAmount?: number
}

interface HTSSelectionResult {
  asin: string
  productTitle: string
  cadValue: number
  usdValue: number
  exchangeRate: number
  countryOfOrigin: string
  htsOptions: HTSOption[]
}

// List of major trading countries and manufacturing hubs
const COUNTRIES = [
  // Major Manufacturing Countries
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'JP', name: 'Japan' },
  { code: 'DE', name: 'Germany' },
  { code: 'KR', name: 'South Korea' },
  { code: 'IT', name: 'Italy' },
  { code: 'FR', name: 'France' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'SG', name: 'Singapore' },
  
  // Southeast Asia Manufacturing
  { code: 'TH', name: 'Thailand' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'LA', name: 'Laos' },
  { code: 'MM', name: 'Myanmar' },
  
  // South Asia
  { code: 'BD', name: 'Bangladesh' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'NP', name: 'Nepal' },
  
  // Middle East & Turkey
  { code: 'TR', name: 'Turkey' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'IL', name: 'Israel' },
  { code: 'IR', name: 'Iran' },
  { code: 'EG', name: 'Egypt' },
  { code: 'MA', name: 'Morocco' },
  { code: 'TN', name: 'Tunisia' },
  
  // Latin America
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'HN', name: 'Honduras' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'PA', name: 'Panama' },
  { code: 'DO', name: 'Dominican Republic' },
  
  // Europe
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'ES', name: 'Spain' },
  { code: 'PT', name: 'Portugal' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'HU', name: 'Hungary' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'HR', name: 'Croatia' },
  { code: 'RO', name: 'Romania' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'GR', name: 'Greece' },
  { code: 'IE', name: 'Ireland' },
  { code: 'DK', name: 'Denmark' },
  { code: 'SE', name: 'Sweden' },
  { code: 'FI', name: 'Finland' },
  { code: 'NO', name: 'Norway' },
  { code: 'IS', name: 'Iceland' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LV', name: 'Latvia' },
  { code: 'EE', name: 'Estonia' },
  { code: 'RU', name: 'Russia' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'BY', name: 'Belarus' },
  
  // Africa
  { code: 'ZA', name: 'South Africa' },
  { code: 'KE', name: 'Kenya' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'GH', name: 'Ghana' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'UG', name: 'Uganda' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MG', name: 'Madagascar' },
  
  // North America & Oceania
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  
  // Other Notable Countries
  { code: 'HK', name: 'Hong Kong' },
  { code: 'MO', name: 'Macau' },
].sort((a, b) => a.name.localeCompare(b.name))

export default function ASINDutyChecker() {
  const [asin, setAsin] = useState('')
  const [countryOfOrigin, setCountryOfOrigin] = useState('')
  const [cadValue, setCadValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [htsSelection, setHtsSelection] = useState<HTSSelectionResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [currentStep, setCurrentStep] = useState('')
  const [cusmaApplicable, setCusmaApplicable] = useState(false)

  // Helper function to check if country is CUSMA-eligible
  const isCusmaCountry = (countryCode: string) => {
    return ['US', 'CA', 'MX'].includes(countryCode)
  }

  // Auto-check CUSMA when country changes to US/CA/MX
  const handleCountryChange = (newCountry: string) => {
    setCountryOfOrigin(newCountry)
    if (isCusmaCountry(newCountry)) {
      setCusmaApplicable(true)
    } else {
      setCusmaApplicable(false)
    }
  }

  const checkDutyRate = async () => {
    if (!asin || !countryOfOrigin || !cadValue) {
      setError('Please fill in all fields')
      return
    }

    if (parseFloat(cadValue) <= 0) {
      setError('CAD value must be greater than 0')
      return
    }

    try {
      setLoading(true)
      setError(null)
      setHtsSelection(null)
      setCurrentStep('Looking up product information...')

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:2999'
      
      // Simulate step updates
      setTimeout(() => setCurrentStep('Classifying HTS codes'), 2000)
      setTimeout(() => setCurrentStep('Getting live exchange rates...'), 8000)
      setTimeout(() => setCurrentStep('Analyzing high-confidence matches...'), 12000)
      
      const headers = requestObfuscation.generateRequestHeaders({
        'Content-Type': 'application/json',
      })

      const response = await fetch(`${API_BASE_URL}/api/public/asin-duty-check`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          asin: asin.trim().toUpperCase(),
          countryOfOrigin,
          cadValue: parseFloat(cadValue)
        }),
      })

      let data = await response.json()
      
      // Deobfuscate response if needed
      data = requestObfuscation.deobfuscateResponse(data)

      if (data.success) {
        if (data.requiresSelection) {
          setCurrentStep('Complete!')
          setHtsSelection(data.data)
        }
      } else {
        setError(data.error || 'Failed to check duty rate')
      }
    } catch (err) {
      console.error('Duty check error:', err)
      setError('Failed to connect to server. Please try again.')
    } finally {
      setLoading(false)
      setCurrentStep('')
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const calculateTotal = (fees: Array<{lineAmount: string | number}>) => {
    return fees.reduce((total, fee) => {
      let amount = 0
      if (typeof fee.lineAmount === 'string') {
        amount = parseFloat(fee.lineAmount.replace(/[^0-9.-]/g, '')) || 0
      } else if (typeof fee.lineAmount === 'number') {
        amount = fee.lineAmount || 0
      }
      return total + amount
    }, 0)
  }

  const parseDutyRate = (dutyRate: string): number => {
    if (!dutyRate || dutyRate === 'N/A' || dutyRate === 'Free' || dutyRate.toLowerCase().includes('free')) {
      return 0
    }
    // Extract percentage from strings like "5.2%", "10%", "$0.05/kg + 3.2%"
    const percentMatch = dutyRate.match(/(\d+(?:\.\d+)?)%/)
    if (percentMatch) {
      return parseFloat(percentMatch[1])
    }
    // If it contains "Free" or "$0" patterns, treat as 0
    if (dutyRate.toLowerCase().includes('free') || dutyRate.includes('$0')) {
      return 0
    }
    return 999 // Default high value for unparseable rates
  }

  const findCheapestDutyRate = (options: HTSOption[]): string | null => {
    if (!options || options.length === 0) return null
    
    let minRate = 999
    let cheapestCode = null
    
    for (const option of options) {
      const rate = parseDutyRate(option.dutyRate)
      if (rate < minRate) {
        minRate = rate
        cheapestCode = option.htsCode
      }
    }
    
    return cheapestCode
  }

  return (
    <div className="card">
      <h2 style={{ marginTop: 0, fontSize: '20px', fontWeight: 'bold', color: '#333' }}>
        ASIN HTS Classifier & Duty Rate Estimator
      </h2>
      <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
        Classify Amazon products with HTS codes and estimate US import duties. No login required, 20 checks per day limit.
      </p>

      {/* Disclaimer Banner */}
      <div style={{
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '6px',
        padding: '12px',
        marginBottom: '20px',
        fontSize: '13px',
        color: '#856404',
        lineHeight: '1.4'
      }}>
        <strong>Disclaimer:</strong> All manufacturing lookup and HTS code suggestions are estimates only and should not be considered accurate or authoritative. This tool is for informational purposes only. You must conduct your own research and verification to ensure compliance with customs regulations. Always consult with qualified customs brokers or trade professionals for official classifications and duty calculations.
      </div>

      <div>
        {/* Input Form */}
        <div>
          <div style={{ display: 'grid', gap: '16px', marginBottom: '20px' }}>
            {/* ASIN Input */}
            <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
            Amazon ASIN
          </label>
          <input
            type="text"
            value={asin}
            onChange={(e) => setAsin(e.target.value)}
            placeholder="e.g., B08N5WRWNW"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>

        {/* Country of Origin */}
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
            Country of Origin
          </label>
          <select
            value={countryOfOrigin}
            onChange={(e) => handleCountryChange(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="">Select country...</option>
            {COUNTRIES.map(country => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
        </div>

        {/* CAD Value */}
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
            Your Cost (CAD)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={cadValue}
            onChange={(e) => setCadValue(e.target.value)}
            placeholder="e.g., 25.99"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
              />
            </div>
          </div>

          <button
            onClick={checkDutyRate}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: loading ? '#6c757d' : '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              marginBottom: '20px'
            }}
          >
            {loading ? (currentStep || 'Classifying & Estimating...') : 'Classify & Estimate Duties'}
          </button>

          {/* Error Display */}
          {error && (
            <div style={{
              padding: '12px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              color: '#721c24',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          )}


        </div>

        {/* HTS Selection (when available) */}
        {htsSelection && (
          <div style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            marginTop: '24px'
          }}>
            {/* Professional Header */}
            <div style={{
              borderBottom: '1px solid #e5e7eb',
              padding: '24px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 12px 0',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>
                HTS Code Classification
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: '12px',
                fontSize: '14px',
                color: '#6b7280',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>
                <div><strong>ASIN:</strong></div>
                <div>{htsSelection.asin}</div>
                <div><strong>Product:</strong></div>
                <div>{htsSelection.productTitle}</div>
              </div>

              {/* CUSMA Banner - Compact version */}
              {isCusmaCountry(htsSelection.countryOfOrigin) && (
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '4px',
                  marginTop: '12px',
                  fontSize: '13px'
                }}>
                  <input
                    type="checkbox"
                    checked={cusmaApplicable}
                    onChange={(e) => setCusmaApplicable(e.target.checked)}
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span style={{ fontWeight: '600', color: '#166534' }}>
                    CUSMA/USMCA Applicable (Duty-Free for US/CA/MX products)
                  </span>
                </label>
              )}
            </div>

            {/* Call to Action */}
            <div style={{
              padding: '20px 24px',
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '6px',
              margin: '24px'
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#166534',
                marginBottom: '8px',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>
                Select Classification for Complete Duty Analysis
              </div>
              <div style={{
                fontSize: '13px',
                color: '#166534',
                lineHeight: '1.5',
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}>
                Choose the most appropriate HTS code to calculate all applicable duties, tariffs, and special assessments including Section 301 and Anti-Dumping fees.
              </div>
            </div>

            {/* Classification Options Grid */}
            <div style={{ padding: '0 24px 24px 24px' }}>
              <div style={{
                display: 'grid',
                gap: '12px'
              }}>
              
                {htsSelection.htsOptions.map((option, index) => {
                  const cheapestCode = findCheapestDutyRate(htsSelection.htsOptions)
                  const isCheapest = option.htsCode === cheapestCode
                  return (
                    <div
                      key={option.htsCode}
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        padding: '16px',
                        backgroundColor: '#ffffff',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                      }}
                    >
                      {/* Header Row */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px'
                      }}>
                        <span style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#1f2937',
                          fontFamily: 'monospace'
                        }}>
                          {option.formattedHtsCode || option.htsCode}
                        </span>
                        
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          {/* Confidence Badge */}
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '500',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            backgroundColor: option.confidence >= 90 
                              ? '#dcfce7' : option.confidence >= 70 
                              ? '#fef3c7' : '#fee2e2',
                            color: option.confidence >= 90 
                              ? '#166534' : option.confidence >= 70 
                              ? '#92400e' : '#991b1b',
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                          }}>
                            {option.confidence}% Match
                          </span>
                          
                          {/* Duty Rate */}
                          {option.dutyRate && option.dutyRate !== 'N/A' && (
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              color: cusmaApplicable ? '#15803d' : '#4b5563',
                              fontFamily: 'system-ui, -apple-system, sans-serif'
                            }}>
                              {cusmaApplicable ? '0% (CUSMA)' : option.dutyRate}
                            </span>
                          )}

                          {/* Lowest Duty Badge */}
                          {isCheapest && (
                            <span style={{
                              fontSize: '11px',
                              fontWeight: '600',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              backgroundColor: '#10b981',
                              color: '#ffffff',
                              fontFamily: 'system-ui, -apple-system, sans-serif'
                            }}>
                              LOWEST
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Description */}
                      <p style={{
                        fontSize: '14px',
                        color: '#4b5563',
                        margin: '0 0 12px 0',
                        lineHeight: '1.4',
                        fontFamily: 'system-ui, -apple-system, sans-serif'
                      }}>
                        {option.fullTitle || option.description}
                      </p>

                      {/* AI Reasoning for top option */}
                      {index === 0 && option.comparativeReasoning && (
                        <div style={{
                          fontSize: '12px',
                          color: '#4b5563',
                          marginBottom: '12px',
                          padding: '12px',
                          backgroundColor: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '4px',
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          lineHeight: '1.4'
                        }}>
                          <strong style={{ color: '#374151' }}>Why this is the best match:</strong><br />
                          {option.comparativeReasoning}
                        </div>
                      )}

                      {/* Duty Display - Similar to ShipmentItemRow */}
                      {option.customsFees && option.customsFees.length > 0 && (
                        <div style={{
                          marginTop: '12px',
                          padding: '12px',
                          backgroundColor: cusmaApplicable ? '#fff3cd' : '#e7f3ff',
                          border: `1px solid ${cusmaApplicable ? '#ffeaa7' : '#b3d9ff'}`,
                          borderRadius: '4px'
                        }}>
                          {/* Header with per-unit cost */}
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            marginBottom: '8px',
                            color: '#374151',
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                          }}>
                            {cusmaApplicable ? 'Duty-Free Item' : 'Estimated Duties + Tariffs'}
                          </div>

                          {/* Cost Per Unit Display */}
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '700',
                            marginBottom: '8px',
                            color: '#1f2937',
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                          }}>
                            {cusmaApplicable ? (
                              '$0.00 CAD per unit (0% of COGS)'
                            ) : htsSelection && option.totalDutyAmount ? (
                              <>
                                ${(option.totalDutyAmount / htsSelection.exchangeRate).toFixed(2)} CAD per unit{' '}
                                <span style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280' }}>
                                  ({((option.totalDutyAmount / htsSelection.usdValue) * 100).toFixed(1)}% of COGS)
                                </span>
                              </>
                            ) : (
                              'N/A'
                            )}
                          </div>

                          {/* Expandable details */}
                          {!cusmaApplicable && (
                            <details style={{ marginTop: '8px' }}>
                              <summary style={{
                                cursor: 'pointer',
                                fontSize: '12px',
                                color: '#4b5563',
                                fontWeight: '500',
                                fontFamily: 'system-ui, -apple-system, sans-serif',
                                userSelect: 'none'
                              }}>
                                View detailed breakdown
                              </summary>

                              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                                {/* Fee line items */}
                                {option.customsFees.map((fee, feeIndex) => (
                                  <div
                                    key={feeIndex}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      fontSize: '12px',
                                      color: '#4b5563',
                                      marginBottom: '4px',
                                      fontFamily: 'system-ui, -apple-system, sans-serif'
                                    }}
                                  >
                                    <span>{fee.lineDescription}</span>
                                    <span style={{ fontWeight: '500' }}>
                                      ${fee.lineAmount.toFixed(2)} USD
                                    </span>
                                  </div>
                                ))}

                                {/* Total in USD */}
                                <div style={{
                                  borderTop: '1px solid #e5e7eb',
                                  marginTop: '8px',
                                  paddingTop: '8px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  color: '#1f2937',
                                  fontFamily: 'system-ui, -apple-system, sans-serif'
                                }}>
                                  <span>Total Duties (USD):</span>
                                  <span>${option.totalDutyAmount?.toFixed(2)}</span>
                                </div>

                                {/* Exchange rate info */}
                                {htsSelection && (
                                  <div style={{
                                    fontSize: '11px',
                                    color: '#6b7280',
                                    textAlign: 'right',
                                    marginTop: '4px',
                                    fontFamily: 'system-ui, -apple-system, sans-serif'
                                  }}>
                                    Exchange rate: 1 USD = {(1 / htsSelection.exchangeRate).toFixed(4)} CAD
                                  </div>
                                )}
                              </div>
                            </details>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  )
}